import authgear, { type UserInfo } from '@authgear/web'

const isLoadingStateKey = 'auth-is-loading'
const isReadyStateKey = 'auth-is-ready'
const userStateKey = 'auth-user'
const accessToken = ref('')

let authConfigurationPromise: Promise<void> | null = null
let sessionInitializationPromise: Promise<void> | null = null

async function configureAuth() {
  const runtimeConfig = useRuntimeConfig()
  const publicVariables = runtimeConfig.public

  await authgear.configure({
    endpoint: publicVariables.AUTHGEAR_ENDPOINT,
    clientID: publicVariables.AUTHGEAR_CLIENT_ID,
    sessionType: 'refresh_token',
  })
}

async function initAuth() {
  if (!authConfigurationPromise) {
    authConfigurationPromise = configureAuth()
  }

  await authConfigurationPromise
}

async function syncAccessToken() {
  accessToken.value = authgear.accessToken ?? ''

  return accessToken.value || null
}

async function refreshAccessTokenIfNeeded() {
  if (!authgear.accessToken) {
    await authgear.refreshAccessTokenIfNeeded()
  }

  return await syncAccessToken()
}

async function getUserInfo() {
  return await authgear.fetchUserInfo()
}

async function loadAuthenticatedUser() {
  await initAuth()

  const token = await refreshAccessTokenIfNeeded()

  if (!token) {
    return null
  }

  return await getUserInfo()
}

export default function useAuth() {
  const route = useRoute()
  const user = useState<UserInfo | null>(userStateKey, () => null)
  const isReady = useState<boolean>(isReadyStateKey, () => false)
  const isLoading = useState<boolean>(isLoadingStateKey, () => false)

  async function login() {
    await initAuth()

    await authgear.startAuthentication({
      redirectURI: `${window.location.origin}/auth/callback`,
    })
  }

  async function ensureSession() {
    if (route.path === '/auth/callback') {
      return
    }

    if (isReady.value) {
      return
    }

    if (!sessionInitializationPromise) {
      sessionInitializationPromise = (async () => {
        isLoading.value = true

        try {
          user.value = await loadAuthenticatedUser()
          isReady.value = true
        } finally {
          isLoading.value = false
          sessionInitializationPromise = null
        }
      })()
    }

    await sessionInitializationPromise
  }

  async function finishLogin() {
    await initAuth()
    await authgear.finishAuthentication()

    await syncAccessToken()
    user.value = await getUserInfo()
    isReady.value = true
  }

  async function logout() {
    await initAuth()

    accessToken.value = ''
    user.value = null
    isReady.value = true

    await authgear.logout({
      redirectURI: `${window.location.origin}/`,
    })
  }

  return {
    ensureSession,
    login,
    finishLogin,
    logout,
    accessToken,
    user,
    isReady,
    isLoading,
  }
}
